/** The signed-in user's name, email and photo, from the shared GET /auth/me profile read. */
import { useMemo } from "react";
import { useOwnProfileQuery } from "./useOwnProfileQuery";
import { toProfileView } from "../services/account/accountProfileMappers";
import { readStoredUserName } from "../services/auth/persistLoginSession";
import { readAccessTokenClaims } from "../services/auth/jwtClaims";
import { ACCESS_TOKEN_KEY } from "../services/api";

export interface CurrentUserProfile {
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
}

const readStored = (key: string): string | null => {
  try {
    return localStorage.getItem(key) || null;
  } catch {
    return null;
  }
};

/** The signed-in user's id, as stored at sign-in; null when absent or storage is blocked. */
export const readCurrentUserId = (): string | null => readStored("userId");

/** The signed-in user's tenant membership id (`users.id`), from the access token; authored records name this one. */
export const readCurrentMembershipId = (): string | null => {
  const token = readStored(ACCESS_TOKEN_KEY);
  return token ? (readAccessTokenClaims(token)?.membershipId ?? null) : null;
};

/** Name and email fall back to the ones stored at sign-in while the profile loads or can't be read. */
export function useCurrentUser(): CurrentUserProfile {
  const { data } = useOwnProfileQuery(toProfileView);
  return useMemo(
    () => ({
      name: data?.displayName || readStoredUserName() || null,
      email: data?.email || readStored("userEmail"),
      avatarUrl: data?.avatarUrl ?? null,
    }),
    [data],
  );
}
